import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

const PEPINO_EMAILS = ['tom@stackbd.com', 'will@stackbd.com', 'tom+dev7@stackbd.com'];

const INITIAL_DELAY_MIN = 10 * 60 * 1000; // 10 minutes
const INITIAL_DELAY_MAX = 30 * 60 * 1000; // 30 minutes
const REPEAT_DELAY_MIN = 15 * 60 * 1000; // 15 minutes
const REPEAT_DELAY_MAX = 45 * 60 * 1000; // 45 minutes
const DANCE_DURATION = 5000; // 5 seconds
const EXIT_DURATION = 1000; // 1 second exit animation

function randomBetween(min, max) {
  return min + Math.random() * (max - min);
}

function isPepinoUser(email) {
  if (!email) return false;
  return PEPINO_EMAILS.includes(email.toLowerCase());
}

const containerStyle = {
  position: 'fixed',
  bottom: 0,
  right: '20px',
  zIndex: 9999,
  pointerEvents: 'none',
};

const pepinoStyle = {
  width: '60px',
  height: '80px',
  cursor: 'pointer',
  pointerEvents: 'auto',
  position: 'relative',
  animation: 'pepino-dance 1s ease-in-out infinite',
};

const cssKeyframes = `
@keyframes pepino-dance {
  0%, 100% { transform: translateY(0) rotate(0deg) scale(1); }
  25% { transform: translateY(-15px) rotate(-10deg) scale(1.05); }
  50% { transform: translateY(-5px) rotate(5deg) scale(0.95); }
  75% { transform: translateY(-20px) rotate(-5deg) scale(1.1); }
}
@keyframes pepino-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes confetti-burst {
  0% { transform: translate(0, 0) scale(1); opacity: 1; }
  100% { transform: translate(var(--confetti-x), var(--confetti-y)) scale(0); opacity: 0; }
}
`;

const CONFETTI_COLORS = ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff6fff', '#ff9f43'];

function ConfettiParticle({ index }) {
  const angle = (index / 8) * Math.PI * 2;
  const distance = 40 + Math.random() * 60;
  const x = Math.cos(angle) * distance;
  const y = Math.sin(angle) * distance - 30;

  return (
    <div
      className="dancing-pepino-confetti"
      style={{
        position: 'absolute',
        width: '8px',
        height: '8px',
        borderRadius: index % 2 === 0 ? '50%' : '0',
        backgroundColor: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
        top: '50%',
        left: '50%',
        '--confetti-x': `${x}px`,
        '--confetti-y': `${y}px`,
        animation: 'confetti-burst 0.6s ease-out forwards',
        pointerEvents: 'none',
      }}
    />
  );
}

function PepinoSVG() {
  return (
    <svg viewBox="0 0 60 80" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
      <ellipse cx="30" cy="45" rx="18" ry="28" fill="#4a7c10" />
      <ellipse cx="30" cy="45" rx="15" ry="25" fill="#6abf1a" />
      <circle cx="20" cy="35" r="3" fill="#4a7c10" opacity="0.4" />
      <circle cx="38" cy="40" r="2.5" fill="#4a7c10" opacity="0.4" />
      <circle cx="25" cy="55" r="2" fill="#4a7c10" opacity="0.4" />
      <ellipse cx="24" cy="35" rx="4" ry="5" fill="white" />
      <ellipse cx="36" cy="35" rx="4" ry="5" fill="white" />
      <circle cx="25" cy="36" r="2" fill="#333" />
      <circle cx="37" cy="36" r="2" fill="#333" />
      <path d="M 22 44 Q 30 52 38 44" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round" />
      <line x1="12" y1="40" x2="4" y2="32" stroke="#4a7c10" strokeWidth="3" strokeLinecap="round" />
      <line x1="48" y1="40" x2="56" y2="32" stroke="#4a7c10" strokeWidth="3" strokeLinecap="round" />
      <line x1="22" y1="70" x2="18" y2="78" stroke="#4a7c10" strokeWidth="3" strokeLinecap="round" />
      <line x1="38" y1="70" x2="42" y2="78" stroke="#4a7c10" strokeWidth="3" strokeLinecap="round" />
      <line x1="30" y1="17" x2="30" y2="10" stroke="#2d5a0a" strokeWidth="2" strokeLinecap="round" />
      <ellipse cx="34" cy="12" rx="5" ry="3" fill="#4a7c10" opacity="0.6" transform="rotate(-20, 34, 12)" />
    </svg>
  );
}

export default function DancingPepino() {
  const auth = useAuth();
  const [visible, setVisible] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const appearTimerRef = useRef(null);
  const danceTimerRef = useRef(null);
  const hasAppeared = useRef(false);

  const email = auth?.user?.profile?.email;
  const isAllowed = !auth.isLoading && isPepinoUser(email);

  // Schedule initial appearance
  useEffect(() => {
    if (!isAllowed) return;
    const delay = randomBetween(INITIAL_DELAY_MIN, INITIAL_DELAY_MAX);
    appearTimerRef.current = setTimeout(() => {
      appearTimerRef.current = null;
      hasAppeared.current = true;
      setVisible(true);
      setClicked(false);
      setShowConfetti(false);
    }, delay);
    return () => {
      if (appearTimerRef.current) {
        clearTimeout(appearTimerRef.current);
        appearTimerRef.current = null;
      }
    };
  }, [isAllowed]);

  // When pepino becomes visible, schedule hide after dance + exit
  useEffect(() => {
    if (!visible) return;
    danceTimerRef.current = setTimeout(() => {
      danceTimerRef.current = null;
      setVisible(false);
    }, DANCE_DURATION + EXIT_DURATION);
    return () => {
      if (danceTimerRef.current) {
        clearTimeout(danceTimerRef.current);
        danceTimerRef.current = null;
      }
    };
  }, [visible]);

  // When pepino hides after having appeared, schedule next cycle
  useEffect(() => {
    if (!isAllowed || visible || !hasAppeared.current) return;
    const delay = randomBetween(REPEAT_DELAY_MIN, REPEAT_DELAY_MAX);
    appearTimerRef.current = setTimeout(() => {
      appearTimerRef.current = null;
      setVisible(true);
      setClicked(false);
      setShowConfetti(false);
    }, delay);
    return () => {
      if (appearTimerRef.current) {
        clearTimeout(appearTimerRef.current);
        appearTimerRef.current = null;
      }
    };
  }, [isAllowed, visible]);

  const handleClick = () => {
    if (clicked) return;
    setClicked(true);
    setShowConfetti(true);
  };

  if (!isAllowed || !visible) return null;

  const classNames = [
    'dancing-pepino',
    'dancing-pepino--dancing',
    clicked ? 'dancing-pepino--spinning' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <style>{cssKeyframes}</style>
      <div className="dancing-pepino-container" style={containerStyle}>
        <div
          className={classNames}
          style={{
            ...pepinoStyle,
            ...(clicked ? { animation: 'pepino-spin 0.5s ease-in-out' } : {}),
          }}
          onClick={handleClick}
        >
          <PepinoSVG />
          {showConfetti && Array.from({ length: 8 }, (_, i) => (
            <ConfettiParticle key={i} index={i} />
          ))}
        </div>
      </div>
    </>
  );
}
